import { ApplicationRef, inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { first, race, timer } from 'rxjs';
import { environment } from '../../../environments/environment';

declare global {
	interface Window {
		clarity?: ClarityFn;
	}
}

type ClarityFn = ((...args: unknown[]) => void) & { q?: unknown[][] };

const CLARITY_PROJECT_ID = 'efp4qv7bue';
const STABILITY_FALLBACK_MS = 5000;
const IDLE_TIMEOUT_MS = 3000;

@Injectable({ providedIn: 'root' })
export class ClarityService {
	private readonly appRef = inject(ApplicationRef);
	private readonly platformId = inject(PLATFORM_ID);
	private loaded = false;

	constructor() {
		if (!isPlatformBrowser(this.platformId) || !environment.isLive) {
			return;
		}

		race(
			this.appRef.isStable.pipe(first((isStable) => isStable === true)),
			timer(STABILITY_FALLBACK_MS),
		).subscribe(() => this.scheduleIdleLoad());
	}

	private scheduleIdleLoad(): void {
		const win = globalThis as Window & typeof globalThis;
		const ric = win.requestIdleCallback?.bind(win);
		if (ric) {
			ric(() => this.loadClarity(), { timeout: IDLE_TIMEOUT_MS });
		} else {
			setTimeout(() => this.loadClarity(), 0);
		}
	}

	private loadClarity(): void {
		if (this.loaded || document.querySelector(`script[src*="clarity.ms/tag/${CLARITY_PROJECT_ID}"]`)) {
			this.loaded = true;
			return;
		}
		this.loaded = true;

		const win = window;
		win.clarity = win.clarity || (function (...args: unknown[]) {
			(win.clarity as ClarityFn).q = (win.clarity as ClarityFn).q || [];
			(win.clarity as ClarityFn).q!.push(args);
		} as ClarityFn);

		const script = document.createElement('script');
		script.async = true;
		script.src = `https://www.clarity.ms/tag/${CLARITY_PROJECT_ID}`;
		const firstScript = document.getElementsByTagName('script')[0];
		firstScript?.parentNode?.insertBefore(script, firstScript);
	}
}
